'use client'
import { useState } from 'react'
import { Card, CardContent } from 'components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select'
import { Badge } from 'components/ui/badge'
import { Button } from 'components/ui/button'
import { Plus, CircleAlert } from 'lucide-react'
import { Link, useNavigate } from 'react-router-dom'
import useListV2 from 'hooks/useListV2'

export function ProjectSalesList() {
  const [sortBy, setSortBy] = useState('latest')
  const navigate = useNavigate()

  const [currentPage, setCurrentPage] = useState(1)
  const { data: projects = [] } = useListV2()

  const summaryData = {
    liveSales: projects.filter((p) => p.status === 'Live').length,
    completedSales: projects.filter((p) => p.status === 'Completed').length,
    totalFundsRaised: projects.reduce((sum, p) => sum + p.totalRaised, 0),
    totalEarned: projects.reduce((sum, p) => sum + p.totalRaised, 0) * 0.025, // 2.5% of totalFundsRaised
  }

  const itemsPerPage = 10

  const getProjectNumericId = (project: any): number => {
    if (!project || project.id === undefined || project.id === null) return 0
    const numericId = Number(project.id)
    return Number.isNaN(numericId) ? 0 : numericId
  }

  const sortedProjects = [...projects].sort((a, b) => {
    switch (sortBy) {
      case 'oldest':
        return getProjectNumericId(a) - getProjectNumericId(b)
      case 'live-first':
        if (
          a.status === 'Live' &&
          a.state !== 'DRAFT' &&
          a.state !== 'REQUESTED_CHANGE' &&
          (b.status !== 'Live' ||
            b.state === 'DRAFT' ||
            b.state === 'REQUESTED_CHANGE')
        )
          return -1
        if (
          (a.status !== 'Live' ||
            a.state === 'DRAFT' ||
            a.state === 'REQUESTED_CHANGE') &&
          b.status === 'Live' &&
          b.state !== 'DRAFT' &&
          b.state !== 'REQUESTED_CHANGE'
        )
          return 1
        return getProjectNumericId(b) - getProjectNumericId(a)
      case 'completed-first':
        if (
          a.status === 'Completed' &&
          a.state !== 'DRAFT' &&
          a.state !== 'REQUESTED_CHANGE' &&
          (b.status !== 'Completed' ||
            b.state === 'DRAFT' ||
            b.state === 'REQUESTED_CHANGE')
        )
          return -1
        if (
          (a.status !== 'Completed' ||
            a.state === 'DRAFT' ||
            a.state === 'REQUESTED_CHANGE') &&
          b.status === 'Completed' &&
          b.state !== 'DRAFT' &&
          b.state !== 'REQUESTED_CHANGE'
        )
          return 1
        return getProjectNumericId(b) - getProjectNumericId(a)
      case 'under-review-first':
        const isAActualUnderReview =
          a.status === 'Under Review' &&
          a.state !== 'DRAFT' &&
          a.state !== 'REQUESTED_CHANGE'
        const isBActualUnderReview =
          b.status === 'Under Review' &&
          b.state !== 'DRAFT' &&
          b.state !== 'REQUESTED_CHANGE'

        if (isAActualUnderReview && !isBActualUnderReview) return -1
        if (!isAActualUnderReview && isBActualUnderReview) return 1
        return getProjectNumericId(b) - getProjectNumericId(a)
      case 'draft-first':
        if (a.state === 'DRAFT' && b.state !== 'DRAFT') return -1
        if (a.state !== 'DRAFT' && b.state === 'DRAFT') return 1
        return getProjectNumericId(b) - getProjectNumericId(a)
      case 'revise-first':
        if (a.state === 'REQUESTED_CHANGE' && b.state !== 'REQUESTED_CHANGE')
          return -1
        if (a.state !== 'REQUESTED_CHANGE' && b.state === 'REQUESTED_CHANGE')
          return 1
        return getProjectNumericId(b) - getProjectNumericId(a)
      case 'latest':
      default:
        return getProjectNumericId(b) - getProjectNumericId(a)
    }
  })

  const indexOfLastItem = currentPage * itemsPerPage
  const indexOfFirstItem = indexOfLastItem - itemsPerPage
  const currentProjects = sortedProjects.slice(
    indexOfFirstItem,
    indexOfLastItem,
  )

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  }

  const formatCurrency = (amount: number) => {
    if (amount >= 1000000) {
      return `$${(amount / 1000000).toFixed(1)}M`
    }
    return `$${(amount / 1000).toFixed(0)}K`
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Live':
        return 'border-[var(--status-success)] text-[var(--status-success)]'
      case 'Completed':
        return 'border-[var(--neutral-400)] text-[var(--neutral-400)]'
      case 'Upcoming':
        return 'border-[var(--status-warning-new)] text-[var(--status-warning-new)]'
      case 'Under Review':
        return 'border-[var(--status-review)] text-[var(--status-review)]'
      default:
        return 'bg-neutral-100 text-neutral-800 border-neutral-200'
    }
  }

  const getStatusBackgroundColor = (status: string) => {
    switch (status) {
      case 'Live':
        return 'bg-[var(--status-successBG)]'
      case 'Completed':
        return 'bg-[var(--neutral-50)]'
      case 'Upcoming':
        return 'bg-[var(--status-warning-newBG)]'
      case 'Under Review':
        return 'bg-[var(--status-reviewBG)]'
      default:
        return 'bg-neutral-100'
    }
  }

  const SaleProgressBar = ({
    totalRaised,
    minRaise,
    maxRaise,
    status,
  }: {
    totalRaised: number
    minRaise: number
    maxRaise: number
    status: string
  }) => {
    const displayRaised = status === 'Upcoming' ? 0 : totalRaised
    const progress = Math.min((displayRaised / maxRaise) * 100, 100)

    // const minProgress = (minRaise / maxRaise) * 100

    return (
      <div className="w-full">
        <div className="flex justify-start text-xs text-neutral-600 mb-1">
          <span>Total Raised: {formatCurrency(displayRaised)}</span>
        </div>
        <div className="w-3/4 bg-neutral-200 rounded-full relative mb-1 h-1.5 overflow-hidden">
          {/* <div
            className="absolute top-0 w-0.5 rounded-full h-1.5 bg-white"
            style={{ left: `${minProgress}%` }}
          /> */}
          <div
            className="bg-blue-500 rounded-full transition-all duration-300 h-1.5"
            style={{ width: `${progress}%` }}
          />
        </div>
        <div className="flex justify-between text-xs text-neutral-500 w-3/4">
          {/* <span>Min: {formatCurrency(minRaise)}</span> */}
          <span>Max: {formatCurrency(maxRaise)}</span>
        </div>
      </div>
    )
  }

  const totalPages = Math.ceil(projects.length / itemsPerPage)

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber)
  }

  const getDisplayedPages = () => {
    const maxVisiblePages = 5
    if (totalPages <= maxVisiblePages) {
      return Array.from({ length: totalPages }, (_, index) => index + 1)
    }
    const pages: Array<number | string> = [1]
    const left = Math.max(2, currentPage - 1)
    const right = Math.min(totalPages - 1, currentPage + 1)

    if (left > 2) {
      pages.push('left-ellipsis')
    }

    for (let page = left; page <= right; page += 1) {
      pages.push(page)
    }

    if (right < totalPages - 1) {
      pages.push('right-ellipsis')
    }

    pages.push(totalPages)
    return pages
  }

  return (
    <div className="min-h-screen bg-neutral-50">
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <h1 className="font-bold text-neutral-900 text-3xl">
              Project Sales
            </h1>
            <Button
              className="bg-brand-primary text-white hover:bg-brand-hover"
              onClick={() => navigate('/submission')}
            >
              Create A Sale
              <Plus className="ml-2 h-4 w-4" />
            </Button>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-6">
                <div className="text-neutral-600 mb-1 text-base font-medium">
                  Live Sales
                </div>
                <div className="text-2xl font-bold text-neutral-900">
                  {summaryData.liveSales}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="text-neutral-600 mb-1 text-base font-medium">
                  Completed Sales
                </div>
                <div className="text-2xl font-bold text-neutral-900">
                  {summaryData.completedSales}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="text-neutral-600 mb-1 text-base font-medium">
                  Total Raised
                </div>
                <div className="text-2xl font-bold text-neutral-900">
                  ${summaryData.totalFundsRaised.toLocaleString()}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="text-neutral-600 mb-1 text-base font-medium">
                  Total Earned
                </div>
                <div className="text-2xl font-bold text-neutral-900">
                  ${summaryData.totalEarned.toLocaleString()}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sort Control */}
          <div className="flex justify-end">
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="latest">Latest → Oldest</SelectItem>
                <SelectItem value="oldest">Oldest → Latest</SelectItem>
                <SelectItem value="live-first">Live First</SelectItem>
                <SelectItem value="completed-first">Completed First</SelectItem>
                <SelectItem value="under-review-first">
                  Under Review First
                </SelectItem>
                <SelectItem value="draft-first">Draft First</SelectItem>
                <SelectItem value="revise-first">Revise First</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="bg-white rounded-lg border border-neutral-200 overflow-hidden">
            <div className="overflow-x-auto">
              {/* Table Header */}
              <div className="grid grid-cols-9 gap-4 px-6 py-4 bg-neutral-50 border-b border-neutral-200 text-sm font-medium text-neutral-600 uppercase tracking-wide min-w-[1100px]">
                <div className="text-xs col-span-2 font-semibold text-neutral-600">
                  PROJECT
                </div>
                <div className="text-xs font-semibold text-neutral-600">
                  TYPE
                </div>
                <div className="text-xs font-semibold text-neutral-600">
                  FDV
                </div>
                <div className="text-xs col-span-2 font-semibold text-neutral-600">
                  SALE PROGRESS
                </div>
                <div className="text-xs font-semibold text-neutral-600">
                  PARTICIPANT
                </div>
                <div className="text-xs font-semibold text-neutral-600">
                  DATE
                </div>
                <div className="text-xs font-semibold text-neutral-600">
                  STATUS
                </div>
              </div>

              {/* Table Body */}
              <div className="divide-y divide-neutral-100">
                {currentProjects.map((project) => (
                  <Link
                    key={project.id}
                    to={`/deals/${project.id}`}
                    // scrollTo={true}
                  >
                    <div className="grid grid-cols-9 gap-4 px-6 py-4 hover:bg-neutral-50 transition-colors cursor-pointer items-center min-w-[1100px]">
                      {/* Project - now spans 2 columns for more space */}
                      <div className="flex items-center gap-3 col-span-2">
                        <div className="w-10 h-10 rounded-sm overflow-hidden bg-neutral-100 flex-shrink-0">
                          <img
                            src={
                              project.projectLogo ||
                              '/placeholder.svg?height=40&width=40'
                            }
                            alt={`${project.projectName} logo`}
                            width={40}
                            height={40}
                            className="w-full h-full object-cover flex-shrink-0"
                          />
                        </div>
                        <span className="font-medium text-neutral-900 text-sm truncate">
                          {project.projectName}
                        </span>
                      </div>

                      {/* Type */}
                      <div className="text-neutral-700 text-sm truncate capitalize">
                        {project.saleType || '—'}
                      </div>

                      {/* FDV */}
                      <div className="text-neutral-700 text-sm truncate">
                        ${project.fdv.toLocaleString()}
                      </div>

                      {/* Sale Progress - spans 2 columns for progress bar */}
                      <div className="col-span-2">
                        <SaleProgressBar
                          totalRaised={project.totalRaised}
                          minRaise={project.minRaise}
                          maxRaise={project.maxRaise}
                          status={project.status}
                        />
                      </div>

                      {/* Participant */}
                      <div className="text-neutral-700 text-sm">
                        {project.participants.toLocaleString()}
                      </div>

                      {/* Date */}
                      <div className="text-neutral-700 text-sm">
                        <div className="flex flex-col">
                          <span className="text-xs">
                            {project.status === 'Upcoming' ||
                            project.status === 'Under Review'
                              ? 'Starts on'
                              : 'Ends on'}
                          </span>

                          <span>
                            {(() => {
                              const timelines = project.timelines || []
                              const hasTimelines = timelines.length > 0
                              const dateValue =
                                project.status === 'Upcoming' ||
                                project.status === 'Under Review'
                                  ? hasTimelines
                                    ? timelines[0].datetime
                                    : null
                                  : hasTimelines
                                  ? timelines[timelines.length - 1].datetime
                                  : null
                              return dateValue ? formatDate(dateValue) : '—'
                            })()}
                          </span>
                        </div>
                      </div>

                      {/* Status */}
                      <div>
                        <div className="flex items-center gap-2">
                          <Badge
                            variant="outline"
                            className={`text-xs px-3 py-1 rounded-sm whitespace-nowrap flex items-center gap-1.5 ${
                              project.state === 'REQUESTED_CHANGE'
                                ? 'border-[var(--status-error)] text-[var(--status-error)] bg-[var(--status-errorBG)]'
                                : project.state === 'DRAFT'
                                ? 'border-neutral-400 text-neutral-400 bg-white'
                                : project.status === 'Completed' &&
                                  !project.tgeDate
                                ? 'border-[var(--status-error)] text-[var(--status-error)] bg-[var(--status-errorBG)]'
                                : `${getStatusColor(
                                    project.status,
                                  )} ${getStatusBackgroundColor(
                                    project.status,
                                  )}`
                            }`}
                          >
                            {project.state === 'REQUESTED_CHANGE' && (
                              <CircleAlert className="w-3.5 h-3.5" />
                            )}
                            {project.status === 'Completed' &&
                              !project.tgeDate &&
                              project.state !== 'REQUESTED_CHANGE' &&
                              project.state !== 'DRAFT' && (
                                <CircleAlert className="w-3.5 h-3.5" />
                              )}
                            {project.state === 'REQUESTED_CHANGE'
                              ? 'Revise'
                              : project.state === 'DRAFT'
                              ? 'Draft'
                              : project.status}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center items-center gap-2 mt-8 flex-wrap">
              {/* Previous Button */}
              <button
                onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className={`flex items-center gap-2 px-3 py-2 text-sm font-medium transition-colors ${
                  currentPage === 1
                    ? 'text-neutral-400 cursor-not-allowed'
                    : 'text-neutral-600 hover:text-neutral-900'
                }`}
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
                Previous
              </button>

              {/* Page Numbers */}
              <div className="flex items-center gap-1 flex-wrap justify-center">
                {getDisplayedPages().map((item, index) => {
                  if (typeof item === 'string') {
                    return (
                      <span
                        key={`${item}-${index}`}
                        className="px-2 text-sm text-neutral-400"
                      >
                        …
                      </span>
                    )
                  }
                  const isActive = currentPage === item
                  return (
                    <button
                      key={item}
                      onClick={() => handlePageChange(item)}
                      className={`w-10 h-10 text-sm font-medium rounded-lg border transition-colors ${
                        isActive
                          ? 'bg-white border-neutral-200 text-neutral-900 shadow-sm'
                          : 'bg-transparent border-transparent text-neutral-600 hover:bg-neutral-50'
                      }`}
                    >
                      {item}
                    </button>
                  )
                })}
              </div>

              {/* Next Button */}
              <button
                onClick={() =>
                  handlePageChange(Math.min(totalPages, currentPage + 1))
                }
                disabled={currentPage === totalPages}
                className={`flex items-center gap-2 px-3 py-2 text-sm font-medium transition-colors ${
                  currentPage === totalPages
                    ? 'text-neutral-400 cursor-not-allowed'
                    : 'text-neutral-900 hover:text-neutral-700'
                }`}
              >
                Next
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
